import React, { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import InteractionHintOverlay from "./InteractionHintOverlay";
import {
    Play,
    Pause,
    Volume2,
    VolumeX,
    Maximize2,
    Minimize2,
    RotateCcw,
    Compass,
} from "lucide-react";
import Hls from "hls.js";
import ViewerFallback from "@/components/ViewerFallback";
import { useHasGyroscope } from "@/lib/useHasGyroscope";
import { hasWebGLSupport } from "@/lib/webgl";
import { useViewerTracking } from "@/lib/viewerTracking";

type Props = {
    src: string;
    /** HLS-Playlist-URL (Same-Origin-Proxy oder direkt R2). Wenn gesetzt und
     * hls.js supported: HLS wird bevorzugt geladen. Fallback: MP4-src. */
    hlsSrc?: string;
    poster?: string;
    testIdPrefix?: string;
    /** Wenn true: WebGL-Init + video.play() werden direkt beim Mount
     *  getriggert (statt erst nach „Erlebnis starten"-Klick). Bewusst nur
     *  für QR-Landing-Kontexte, wo Instant-Immersion erwünscht ist. Ton
     *  bleibt stumm (Browser-Autoplay-Policy). Default false. */
    autoPlay?: boolean;
    /** Wenn true: startet automatisch die Auto-Rotate-Tour, sobald das Video
     *  spielt und der User noch nicht interagiert hat. Respektiert
     *  `prefers-reduced-motion`. Kann jederzeit über den Tour-Button
     *  ein-/ausgeschaltet werden. Default false. */
    autoTour?: boolean;
    /** Analytics: stabile Media-ID (aus DB). Wenn nicht gesetzt: hash(src). */
    mediaId?: string;
    /** Optionaler menschenlesbarer Titel für Admin-Dashboards. */
    mediaTitle?: string;
    /** Projekt-ID, falls das Video einem Projekt zugeordnet ist. */
    projectId?: string;
    /** Wenn true: Der Viewer-Shell nutzt `w-full h-full` statt fixem
     *  Aspect-Ratio (16:9 / 21:9). Sinnvoll wenn der Parent-Container die
     *  Größe bereits vorgibt (z.B. Vergleichs-Kachel mit 4:3-Aspect).
     *  Der WebGL-Canvas passt sich per ResizeObserver an. Default false. */
    fillParent?: boolean;
    /** Wenn true (Default): Video wird geloopt. In Slideshow-Kontexten auf
     *  `false` setzen, sodass `ended` feuert und die äußere Slideshow weiterläuft. */
    loop?: boolean;
    /** Wird gefeuert wenn das interne `<video>`-Element sein `ended`-Event
     *  auslöst (nur relevant wenn `loop=false`). */
    onEnded?: () => void;
    /** Slideshow-Kontext: Signalisiert ob dieser Viewer der aktuell sichtbare
     *  Slide ist. Bei Wechsel false→true: `currentTime=0 + play()`. Ohne
     *  diesen Prop läuft das Video normal weiter (Default: true). */
    isActive?: boolean;
};

/**
 * 360°-Video-Viewer (Three.js).
 *
 * WICHTIG: Three.js/WebGL wird **lazy** initialisiert.
 * - Beim Mount rendert die Komponente nur das Poster + "Erlebnis starten"-Button.
 * - Erst wenn der Nutzer klickt, wird `isWebGLAvailable()` geprüft und der
 *   WebGLRenderer in try/catch erzeugt.
 * - So gibt es beim reinen Mount der Komponente genau EINEN aktiven WebGL-Kontext
 *   (den vom Photo Sphere Viewer) — kein Ressourcen-Konflikt im iframe.
 * - Bei WebGL-Fehler wird die Fallback-Karte gerendert (kein React-Crash-Overlay).
 *
 * HLS-Streaming:
 * - Wenn `hlsSrc` gesetzt: MSE (via hls.js) wird verwendet. Three.js sieht das
 *   `<video>`-Element unverändert — hls.js schreibt via MediaSource Extensions.
 * - Safari (iOS/macOS) hat Native-HLS: direkt `<video src={hlsSrc}>`.
 * - Chrome/Firefox: hls.js.
 */
export default function Video360Viewer({
    src,
    hlsSrc,
    poster,
    testIdPrefix = "video360",
    autoPlay = false,
    autoTour = false,
    mediaId,
    mediaTitle,
    projectId,
    fillParent = false,
    loop = true,
    onEnded,
    isActive = true,
}: Props) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    // Slideshow-Slide-Steuerung: bei Wechsel false→true das Video von vorn
    // starten. `isFirstRun` verhindert dass wir beim initialen Mount in den
    // internen HLS/MP4-Attachment-Flow eingreifen (dort ist noch keine Source
    // am `<video>`-Element → `play()` würde `NotSupportedError` werfen und
    // im Standalone-Modus den kompletten Viewer via ErrorBoundary fangen).
    // Nur bei nachträglichen `isActive`-Toggles (Slideshow-Kontext) wird
    // tatsächlich reset+play/pause ausgeführt.
    const isActiveFirstRun = useRef(true);
    useEffect(() => {
        if (isActiveFirstRun.current) {
            isActiveFirstRun.current = false;
            return;
        }
        const v = videoRef.current;
        if (!v) return;
        if (isActive) {
            try { v.currentTime = 0; } catch { /* Firefox InvalidStateError ignorieren */ }
            void v.play().catch(() => undefined);
        } else {
            try { v.pause(); } catch { /* ignore */ }
            try { v.currentTime = 0; } catch { /* ignore */ }
        }
    }, [isActive]);

    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const textureRef = useRef<THREE.VideoTexture | null>(null);
    const posterTextureRef = useRef<THREE.Texture | null>(null);
    const geometryRef = useRef<THREE.SphereGeometry | null>(null);
    const materialRef = useRef<THREE.MeshBasicMaterial | null>(null);
    const rafRef = useRef<number | null>(null);
    const rvfcHandleRef = useRef<number | null>(null);

    const lonRef = useRef(180);
    const latRef = useRef(0);
    const fovRef = useRef(75);
    const gyroActiveRef = useRef(false);
    const gyroHandlerRef = useRef<((e: DeviceOrientationEvent) => void) | null>(
        null,
    );

    // Cinematic Auto-Rotate: nach Reset läuft ein rAF-Loop, der lonRef
    // kontinuierlich rotiert (~12°/s ≈ 2rpm) mit sanftem 1s Ease-In und
    // Sinus-Pitch-Wave (±3° über 8s). Der 3D-Renderloop (animate() weiter
    // unten) liest lonRef/latRef bei jedem Frame — wir müssen nur die
    // Werte animieren.
    const autoRotatingRef = useRef<boolean>(false);
    const autoRotateRafRef = useRef<number | null>(null);
    const autoRotateStartAngleRef = useRef<{ lon: number; lat: number }>({
        lon: 180,
        lat: 0,
    });
    const [autoRotating, setAutoRotating] = useState<boolean>(false);
    /** Auto-Resume-Timer (5 s Idle nach Canvas-Drag). Identisches Muster
     *  wie im BeforeAfter360Viewer: Slider/Controls berühren Timer nicht,
     *  nur echter Canvas-Drag pausiert Tour und startet Resume-Timer. */
    const autoResumeTimerRef = useRef<number | null>(null);

    // Video-Timeline: elapsed/duration. Updates via `timeupdate`/`loadedmetadata`
    // events auf dem <video>-Element. Rendern nur wenn duration > 0.
    const [duration, setDuration] = useState<number>(0);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const timelineRef = useRef<HTMLDivElement | null>(null);
    const seekingRef = useRef<boolean>(false);

    const stopAutoRotate = useCallback(() => {
        if (autoRotateRafRef.current !== null) {
            cancelAnimationFrame(autoRotateRafRef.current);
            autoRotateRafRef.current = null;
        }
        autoRotatingRef.current = false;
        setAutoRotating(false);
    }, []);

    /** Cancelt einen laufenden Auto-Resume-Timer ohne die Rotation zu
     *  starten/stoppen. Wird bei neuem Canvas-Drag oder Video-Pause gerufen. */
    const cancelAutoResume = useCallback(() => {
        if (autoResumeTimerRef.current !== null) {
            window.clearTimeout(autoResumeTimerRef.current);
            autoResumeTimerRef.current = null;
        }
    }, []);

    // Wiederverwendbarer Gyro-Detach: von toggleGyro (OFF-Zweig), von der
    // Mutual-Exclusion-Guard in startAutoRotate, und vom Unmount-Cleanup
    // aufgerufen. useCapture=true muss beim Remove symmetrisch sein.
    const detachGyro = useCallback(() => {
        if (gyroHandlerRef.current) {
            window.removeEventListener(
                "deviceorientation",
                gyroHandlerRef.current,
                true,
            );
            gyroHandlerRef.current = null;
        }
        gyroActiveRef.current = false;
        setGyroOn(false);
    }, []);

    const startAutoRotate = useCallback(() => {
        // Accessibility: prefers-reduced-motion → kein Auto-Rotate
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            console.log("[Video360Viewer] auto-rotate skipped (prefers-reduced-motion)");
            return;
        }
        // Mutual Exclusion: Gyro aktiv? → sofort abschalten, sonst überschreibt
        // der deviceorientation-Handler die rAF-Kamera bei jedem Sensor-Event.
        if (gyroActiveRef.current) {
            detachGyro();
        }
        autoRotatingRef.current = true;
        setAutoRotating(true);
        autoRotateStartAngleRef.current = {
            lon: lonRef.current,
            lat: latRef.current,
        };
        const t0 = performance.now();
        const YAW_DEG_PER_S = 12; // 2 RPM
        const PITCH_AMP_DEG = 3;
        const PITCH_PERIOD_S = 8;
        const startLon = autoRotateStartAngleRef.current.lon;
        const startLat = autoRotateStartAngleRef.current.lat;
        const tick = (now: number) => {
            if (!autoRotatingRef.current) return;
            const elapsed = (now - t0) / 1000;
            const ease = Math.min(elapsed / 1.0, 1); // 1s Ease-In
            // Angular velocity ramps 0 → YAW_DEG_PER_S linear über 1s.
            // Integrierter Winkelfortschritt = YAW_DEG_PER_S * ease * elapsed
            // (bei t=1s: 1*1*12 = 12°; ab da linear mit 12°/s).
            const yawDelta = YAW_DEG_PER_S * ease * elapsed;
            lonRef.current = (startLon + yawDelta) % 360;
            const pitchWave =
                PITCH_AMP_DEG *
                Math.sin((elapsed * 2 * Math.PI) / PITCH_PERIOD_S) *
                ease;
            latRef.current = Math.max(
                -85,
                Math.min(85, startLat + pitchWave),
            );
            autoRotateRafRef.current = requestAnimationFrame(tick);
        };
        autoRotateRafRef.current = requestAnimationFrame(tick);
    }, [detachGyro]);

    /** Startet einen 5-s-Auto-Resume-Timer. Nach Ablauf `startAutoRotate()`,
     *  sofern Video noch läuft (nicht paused/ended), kein Gyro aktiv, und
     *  Tour nicht bereits läuft. Wird typischerweise von `pointerup` nach
     *  echtem Canvas-Drag oder von `wheel` gerufen. */
    const scheduleAutoResume = useCallback(() => {
        cancelAutoResume();
        autoResumeTimerRef.current = window.setTimeout(() => {
            autoResumeTimerRef.current = null;
            if (gyroActiveRef.current) return;
            if (autoRotatingRef.current) return;
            // Video muss aktiv sein — pausiertes/beendetes Video darf keine
            // unerwartete Auto-Tour im Hintergrund starten.
            const v = videoRef.current;
            if (!v || v.paused || v.ended) return;
            startAutoRotate();
        }, 5000);
    }, [cancelAutoResume, startAutoRotate]);

    // Cleanup rAF bei Unmount / initialized-Wechsel
    useEffect(() => {
        return () => {
            if (autoRotateRafRef.current !== null) {
                cancelAnimationFrame(autoRotateRafRef.current);
                autoRotateRafRef.current = null;
            }
            autoRotatingRef.current = false;
        };
    }, []);

    // Cleanup Auto-Resume-Timer bei Unmount
    useEffect(() => () => cancelAutoResume(), [cancelAutoResume]);

    const [initialized, setInitialized] = useState(false);
    const [initError, setInitError] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [gyroOn, setGyroOn] = useState(false);
    const [videoReady, setVideoReady] = useState(false);
    /** Wird gesetzt, sobald das <video>-Element WIRKLICH einen ersten Frame
     *  ausgemalt hat — nicht nur `canplay` (das feuert bevor gerendert wird).
     *  Steuert das Ausfaden des Poster-Overlays. Detection primary via
     *  `requestVideoFrameCallback` (Chrome/Edge/Safari 17+), Fallback: 150 ms
     *  nach `playing`-Event. Failsafe: 15 s Timeout gemeinsam mit videoReady. */
    const [firstFramePainted, setFirstFramePainted] = useState(false);
    const [mediaError, setMediaError] = useState<string | null>(null);
    /** Wenn HLS im aktuellen Browser fatal fehlschlägt (z.B. Chromium ohne
     *  H.264-in-MSE-Codec), zwingt dieses Flag `effectiveHlsSrc` auf
     *  undefined → der MP4-useEffect (weiter unten) greift und lädt die
     *  direkte MP4-src via `<video>`. Verhindert stilles Scheitern. */
    const [hlsFatalFallback, setHlsFatalFallback] = useState(false);
    const [needsPlayTap, setNeedsPlayTap] = useState(false);
    const [autoHlsSrc, setAutoHlsSrc] = useState<string | undefined>(undefined);
    // Zeigt "Wird für Web-Wiedergabe optimiert"-Overlay wenn Source > 4K und
    // HLS noch nicht bereit ist. Aus /info abgeleitet.
    const [needsOptimization, setNeedsOptimization] = useState(false);
    /** Race-Guard: verhindert Redundant-Reloads wenn `src` sich technisch
     *  ändert aber logisch identisch ist (z.B. React-Rerender mit gleicher
     *  URL). Wird im MP4-useEffect verglichen und bei echter Änderung
     *  aktualisiert. */
    const lastSrcRef = useRef<string>("");

    // Analytics: 360°-Viewer-Tracking (Open, Play, Pause, Complete,
    // Milestones, Fullscreen, Interaction, Close). Bei Admin-Routen inaktiv.
    const tracker = useViewerTracking({
        viewerType: "video360",
        mediaId,
        mediaTitle: mediaTitle || src,
        projectId,
    });

    /** Aktuell "beabsichtigte" Video-Src. Wird bei jedem safeAutoplay-Aufruf
     *  ankommt, kann der erste anhand des Vergleichs merken, dass er nicht
     *  mehr aktuell ist und leise abbrechen — ohne AbortError-Warnung. */
    const currentSrcRef = useRef<string>("");
    /** In-Flight play()-Promise. Zweite Aufrufe warten bis der erste settled
     *  ist. Verhindert „The play() request was interrupted by a call to
     *  pause()/load()" wenn zwei Play-Trigger sich überlappen. */
    const playInFlightRef = useRef<Promise<void> | null>(null);
    /** Zentrale, race-safe Play-Funktion:
     *  - Serialisiert überlappende play()-Aufrufe (wartet auf in-flight play)
     *  - Prüft currentSrcRef: bei zwischenzeitlichem Src-Wechsel silent-abort
     *  - Wartet auf `canplay` wenn Video noch nicht ready
     *  - Fängt AbortError still ab (harmlos: neuer load() unterwegs)
     *  - Fängt NotAllowedError → `needsPlayTap`-Overlay
     *  - Alles andere → `console.warn`, kein setMediaError (damit der
     *    ehrliche Codec-Overlay nicht überschrieben wird) */
    const safeAutoplay = useCallback(async (v: HTMLVideoElement) => {
        if (!v) return;
        // Serialisierung: falls ein vorheriger play() noch offen ist, warten.
        if (playInFlightRef.current) {
            try {
                await playInFlightRef.current;
            } catch {
                /* das darf gescheitert sein — wir starten trotzdem einen neuen */
            }
        }
        const targetSrc = v.currentSrc || v.src || "";
        currentSrcRef.current = targetSrc;

        if (v.readyState < 3) {
            await new Promise<void>((resolve) => {
                const handler = () => resolve();
                v.addEventListener("canplay", handler, { once: true });
                // Timeout-Guard: wenn canplay nie feuert (z.B. Codec-Fail),
                // nach 5s aufgeben.
                window.setTimeout(() => {
                    v.removeEventListener("canplay", handler);
                    resolve();
                }, 5000);
            });
        }
        // Zwischenzeitlicher Src-Wechsel: dieser play-Versuch ist obsolet.
        const nowSrc = v.currentSrc || v.src || "";
        if (currentSrcRef.current !== targetSrc || (nowSrc && nowSrc !== targetSrc)) {
            return;
        }
        try {
            const p = v.play();
            playInFlightRef.current = p;
            await p;
            setNeedsPlayTap(false);
        } catch (err) {
            const name = err instanceof Error ? err.name : String(err);
            if (name === "AbortError") return;  // harmlos, neuer Load unterwegs
            if (name === "NotAllowedError") {
                setNeedsPlayTap(true);
                return;
            }
            console.warn("[Video360Viewer] play failed:", err);
        } finally {
            playInFlightRef.current = null;
        }
    }, []);
    // Info-Fetch abgeschlossen? Für Race-Condition-Vermeidung: wenn src auf
    // /api/media/{uuid} zeigt, warten wir mit dem Setzen des <video src>-
    // Attributs, bis /info geantwortet hat. Sonst würde der Browser sofort
    // die (evtl. undekodierbare) 8K-MP4 laden und error.code=4 werfen,
    // bevor der HLS-Wiring überhaupt eine Chance hatte.
    const [infoLoaded, setInfoLoaded] = useState(false);
    const hasApiMediaId = /\/(?:api\/)?media\/[a-f0-9]{8}-/i.test(src);
    // Interaction-Hint-Overlay: erscheint sobald der User „Erlebnis starten"
    // klickt (initialized=true) und bleibt permanent sichtbar solange der
    // Viewer aktiv ist. Kein Auto-Dismiss, kein Interaktions-Dismiss —
    // pointer-events-none stellt sicher dass Drag/Zoom trotzdem durchgehen.

    // Auto-HLS: extrahiere media_id aus src (falls es eine /api/media/{uuid}-URL
    // ist) und frage /info. Wenn hls_ready=true → HLS-Proxy-URL als hlsSrc setzen.
    // Wenn Source-Breite > 4096 aber HLS noch nicht ready → Optimierungs-Overlay.
    //
    // Direkte .m3u8-URL im src → sofort als hlsSrc verwenden (kein /info-Call).
    useEffect(() => {
        if (hlsSrc) {
            setInfoLoaded(true);
            return;
        }
        // Path A: src ist bereits eine .m3u8-URL (CMS zeigt direkt auf R2 CDN)
        if (/\.m3u8($|\?)/i.test(src)) {
            setAutoHlsSrc(src);
            setInfoLoaded(true);
            return;
        }
        const m = src.match(
            /\/(?:api\/)?media\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i,
        );
        const mediaId = m?.[1];
        if (!mediaId) {
            setInfoLoaded(true);
            return;
        }
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const ctrl = new AbortController();
        (async () => {
            try {
                const r = await fetch(`${base}/api/media/${mediaId}/info`, {
                    credentials: "omit",
                    signal: ctrl.signal,
                });
                if (!r.ok) {
                    setInfoLoaded(true);
                    return;
                }
                const info = (await r.json()) as {
                    hls_ready?: boolean;
                    hls_playlist_url?: string;
                    hls_source_width?: number;
                    width?: number;
                };
                if (info.hls_ready && info.hls_playlist_url) {
                    setAutoHlsSrc(`${base}${info.hls_playlist_url}`);
                } else {
                    const w = info.hls_source_width || info.width || 0;
                    if (w > 4096) setNeedsOptimization(true);
                }
                setInfoLoaded(true);
            } catch (err) {
                // AbortError (Component unmount) → silent
                if ((err as { name?: string })?.name === "AbortError") return;
                setInfoLoaded(true);
            }
        })();
        return () => ctrl.abort();
    }, [src, hlsSrc]);

    const effectiveHlsSrc = hlsFatalFallback ? undefined : (hlsSrc || autoHlsSrc);
    // Nur MP4-src rendern wenn kein HLS aktiv UND /info-Fetch fertig (oder
    // URL sowieso kein Media-Endpoint). Sonst räumt der Browser das 8K-MP4
    // schon vor der HLS-Attach.
    const shouldRenderMp4Src =
        !effectiveHlsSrc && (!hasApiMediaId || infoLoaded);

    // Interaction-Hint bleibt permanent sichtbar sobald der Viewer
    // initialisiert ist. `initialized` als direkter Render-Trigger reicht —
    // kein zusätzlicher State, kein Timeout, kein Dismiss.

    // Test-Hook: expose per-instance getYaw/getLat/getVideo/isTouring/
    // isPlaying auf `window.__hf_vid360__[testIdPrefix]` (analog zum
    // BeforeAfter360Viewer-Pattern). Mount-Counter in
    // `__hf_vid360_mounts__` erlaubt Re-Mount-Regression-Assertions.
    // Legacy-Alias `window.__hf_video360__` bleibt für Backward-Compat
    // (letzte Instanz gewinnt) — nutze das prefix-Dict für Multi-Instance.
    useEffect(() => {
        const key = testIdPrefix ?? "default";
        const w = window as unknown as {
            __hf_video360__?: { getLon: () => number; getLat: () => number; getVideo: () => HTMLVideoElement | null };
            __hf_vid360__?: Record<string, {
                getYaw: () => number;
                getLat: () => number;
                getVideo: () => HTMLVideoElement | null;
                isTouring: () => boolean;
                isPlaying: () => boolean;
            }>;
            __hf_vid360_mounts__?: Record<string, number>;
        };
        if (!w.__hf_vid360__) w.__hf_vid360__ = {};
        if (!w.__hf_vid360_mounts__) w.__hf_vid360_mounts__ = {};
        const api = {
            getYaw: () => lonRef.current,
            getLat: () => latRef.current,
            getVideo: () => videoRef.current,
            isTouring: () => autoRotatingRef.current,
            isPlaying: () => {
                const v = videoRef.current;
                return !!(v && !v.paused && !v.ended);
            },
        };
        w.__hf_vid360__[key] = api;
        w.__hf_vid360_mounts__[key] = (w.__hf_vid360_mounts__[key] || 0) + 1;
        // Legacy Single-Instance-Hook: erlaubt bestehende Tests weiter.
        w.__hf_video360__ = {
            getLon: () => lonRef.current,
            getLat: () => latRef.current,
            getVideo: () => videoRef.current,
        };
        return () => {
            if (w.__hf_vid360__ && w.__hf_vid360__[key]) {
                delete w.__hf_vid360__[key];
            }
            if (w.__hf_video360__) delete w.__hf_video360__;
        };
    }, [testIdPrefix]);

    // iOS-Legacy Attribut: webkit-playsinline muss als DOM-Attribut gesetzt
    // sein (kein React-Prop). Auf modernem Safari nicht schädlich.
    useEffect(() => {
        const v = videoRef.current;
        if (v) {
            v.setAttribute("webkit-playsinline", "true");
            v.setAttribute("x5-playsinline", "true");
            v.setAttribute("x5-video-player-type", "h5");
        }
    }, []);

    // HLS-Attachment (nur wenn effectiveHlsSrc gesetzt).
    // - Safari: Native-HLS → einfach v.src = hlsSrc setzen.
    // - Chrome/Firefox: hls.js via MediaSource Extensions.
    // Three.js sieht das <video>-Element unverändert und picked die Frames
    // via VideoTexture unabhängig davon ob Content aus MP4 oder MSE kommt.
    //
    // Bei Fatal-Error wird KEIN Fallback auf den Original-MP4-Src versucht,
    // da HLS für Videos > 4K Breite triggert und die Original-Quelle in dem
    // Fall vom Browser sowieso nicht dekodiert werden könnte.
    useEffect(() => {
        const v = videoRef.current;
        if (!v || !effectiveHlsSrc) return;
        let hls: Hls | null = null;
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            const canNativeHls = v.canPlayType("application/vnd.apple.mpegurl") !== "";
            if (canNativeHls) {
                v.src = effectiveHlsSrc;
                v.load();
                return;
            }
            if (!Hls.isSupported()) {
                console.warn("[Video360Viewer] hls.js nicht verfügbar, HLS wird geskipped");
                return;
            }
            hls = new Hls({
                enableWorker: true,
                lowLatencyMode: false,
                backBufferLength: 30,
                // Explizit: keine Credentials, keine Cookies mitschicken. Sonst
                // triggert der Preview-Overlay-Fetch-Wrapper CORS-Probleme.
                xhrSetup: (xhr) => {
                    xhr.withCredentials = false;
                },
            });
            hls.loadSource(effectiveHlsSrc);
            hls.attachMedia(v);
            hls.on(Hls.Events.ERROR, (_evt, data) => {
                if (!data.fatal) {
                    // hls.js versucht selbst nicht-fatale Errors zu recovern.
                    return;
                }
                // Fatal: MSE-Codec-Reject (Chromium ohne H.264 in MSE) oder
                // Netzwerk-Timeout. Anstatt einen roten Error-Overlay zu zeigen,
                // fallen wir auf die direkte MP4-`src` zurück — die läuft via
                // nativem `<video>`-Element unabhängig von MSE.
                console.warn(
                    "[Video360Viewer] HLS fatal, fallback → MP4:",
                    data.type, data.details,
                );
                try { hls?.destroy(); } catch { /* ignore */ }
                hls = null;
                if (src) {
                    // MP4-Fallback aktivieren: `effectiveHlsSrc` wird
                    // undefined, der MP4-useEffect greift automatisch.
                    setHlsFatalFallback(true);
                    setMediaError(null);
                } else {
                    // Kein MP4-Fallback verfügbar → Error-Overlay als letztes
                    // Mittel (praktisch: HeroCarousel/SmartMedia liefern
                    // immer `src` mit).
                    const detail = data.details || data.type || "unbekannter Fehler";
                    setMediaError(
                        `Wiedergabe nicht möglich (${detail}). Bitte Browser aktualisieren.`,
                    );
                }
            });
        })();
        return () => {
            cancelled = true;
            try { hls?.destroy(); } catch { /* ignore */ }
        };
    }, [effectiveHlsSrc, src]);

    // Fullscreen State
    useEffect(() => {
        const onFs = () =>
            setIsFullscreen(Boolean(document.fullscreenElement));
        document.addEventListener("fullscreenchange", onFs);
        return () => document.removeEventListener("fullscreenchange", onFs);
    }, []);

    // Source-Wechsel: wenn sich `src` ändert (Gallery-Wechsel, CMS-Update),
    // muss das `<video>`-Element aktiv neu geladen werden. Ohne `.load()`
    // ignoriert der Player die neue src-Prop-Zuweisung durch React.
    // Wird NICHT gerufen wenn HLS aktiv ist — das dortige useEffect (Zeile 104)
    // übernimmt bereits die Neu-Attachment-Logik.
    useEffect(() => {
        const v = videoRef.current;
        if (!v || !src || effectiveHlsSrc) return;
        // Race-Guard: gleiche src → kein Reload. Sonst würde jeder Rerender
        // ein neues .load() triggern und laufende .play()-Promises abbrechen
        // („The play() request was interrupted by a new load request").
        if (src === lastSrcRef.current) return;
        lastSrcRef.current = src;
        try {
            v.load();
            setVideoReady(false);
            setFirstFramePainted(false);
            setNeedsPlayTap(false);
            setMediaError(null);
        } catch (e) {
            console.warn("[Video360Viewer] video.load() fehlgeschlagen", e);
        }
    }, [src, effectiveHlsSrc]);

    // -------- Lazy Three.js Init --------
    useEffect(() => {
        if (!initialized) return;
        const container = containerRef.current;
        const canvas = canvasRef.current;
        const video = videoRef.current;
        if (!container || !canvas || !video) return;

        let renderer: THREE.WebGLRenderer | null = null;
        try {
            renderer = new THREE.WebGLRenderer({
                canvas,
                antialias: false,
                alpha: false,
                powerPreference: "default",
                failIfMajorPerformanceCaveat: false,
                preserveDrawingBuffer: false,
            });
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            // Diagnose: raw context probing für besseren Log
            let ctxInfo = "";
            try {
                const probeCanvas = document.createElement("canvas");
                const gl2 = probeCanvas.getContext("webgl2");
                const gl1 = probeCanvas.getContext("webgl");
                const glE = probeCanvas.getContext("experimental-webgl");
                ctxInfo = `webgl2=${!!gl2} webgl=${!!gl1} exp=${!!glE}`;
            } catch {
                /* ignore */
            }
            console.warn(
                "[Video360Viewer] WebGL init failed:",
                msg,
                ctxInfo,
            );
            setInitError(msg);
            setInitialized(false);
            return;
        }

        const width = container.clientWidth;
        const height = container.clientHeight;
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(width, height, false);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        rendererRef.current = renderer;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(
            fovRef.current,
            width / height,
            0.1,
            1100,
        );
        camera.position.set(0, 0, 0.01);

        const geometry = new THREE.SphereGeometry(500, 64, 40);
        geometry.scale(-1, 1, 1);

        // Poster als initiale Sphere-Textur laden. Damit sieht der User sofort
        // eine begehbare 360°-Szene (kein schwarzer Bildschirm) während das
        // Video noch lädt. Poster ist ein equirektangulär gerenderter Frame
        // aus dem Backend (bei 360°-Videos rectilinear-untransformierter
        // Original-Equirect) und mapped 1:1 auf die Sphere.
        // Fallback: wenn kein Poster verfügbar oder Load fehlschlägt → direkt
        // VideoTexture (alter Verhalten).
        // Sphere startet mit VideoTexture. Bis das Video den ersten Frame
        // gemalt hat, ist die Sphere im Hintergrund durch das Poster-Overlay
        // (Layer 1, DOM `<img>`) verdeckt — deshalb muss die Sphere selbst
        // KEINE Poster-Textur laden (das würde nur den WebGL-Init verzögern
        // und CORS-Anforderungen an den Poster-URL stellen).
        const videoTexture = new THREE.VideoTexture(video);
        videoTexture.colorSpace = THREE.SRGBColorSpace;
        videoTexture.minFilter = THREE.LinearFilter;
        videoTexture.magFilter = THREE.LinearFilter;
        videoTexture.generateMipmaps = false;
        videoTexture.format = THREE.RGBAFormat;
        textureRef.current = videoTexture;

        // MeshBasicMaterial mit VideoTexture. Solange kein Frame gemalt ist,
        // zeigt die Sphere schwarz — aber das Overlay verdeckt sie komplett
        // (opacity: 1), bis `firstFramePainted` triggert und ein sanfter
        // 400 ms Cross-Fade zum echten Video-Frame stattfindet.
        const material = new THREE.MeshBasicMaterial({ map: videoTexture });
        const sphere = new THREE.Mesh(geometry, material);
        scene.add(sphere);

        sceneRef.current = scene;
        cameraRef.current = camera;
        geometryRef.current = geometry;
        materialRef.current = material;

        // ---- iOS-safe Frame Update ----
        // iOS Safari aktualisiert VideoTexture nicht automatisch zuverlässig.
        // Wir triggern texture.needsUpdate=true synchron zu Video-Frames,
        // bevorzugt via requestVideoFrameCallback, sonst RAF-Fallback.
        type RVFCVideo = HTMLVideoElement & {
            requestVideoFrameCallback?: (
                cb: (now: number, meta: unknown) => void,
            ) => number;
            cancelVideoFrameCallback?: (id: number) => void;
        };
        const rvfcVideo = video as RVFCVideo;
        const useRVFC =
            typeof rvfcVideo.requestVideoFrameCallback === "function";
        const onVideoFrame = () => {
            if (video.readyState >= 2 && textureRef.current) {
                textureRef.current.needsUpdate = true;
            }
            if (useRVFC && rvfcVideo.requestVideoFrameCallback) {
                rvfcHandleRef.current = rvfcVideo.requestVideoFrameCallback(
                    onVideoFrame,
                );
            }
        };
        if (useRVFC && rvfcVideo.requestVideoFrameCallback) {
            rvfcHandleRef.current = rvfcVideo.requestVideoFrameCallback(
                onVideoFrame,
            );
        }

        const animate = () => {
            if (!rendererRef.current || !cameraRef.current || !sceneRef.current)
                return;
            // RAF-Fallback: Wenn rVFC nicht verfügbar, hier Textur aktualisieren
            if (!useRVFC && video.readyState >= 2 && textureRef.current) {
                textureRef.current.needsUpdate = true;
            }
            const phi = THREE.MathUtils.degToRad(90 - latRef.current);
            const theta = THREE.MathUtils.degToRad(lonRef.current);
            const target = new THREE.Vector3(
                500 * Math.sin(phi) * Math.cos(theta),
                500 * Math.cos(phi),
                500 * Math.sin(phi) * Math.sin(theta),
            );
            cameraRef.current.lookAt(target);
            cameraRef.current.fov = fovRef.current;
            cameraRef.current.updateProjectionMatrix();
            try {
                rendererRef.current.render(
                    sceneRef.current,
                    cameraRef.current,
                );
            } catch (err) {
                console.warn("[Video360Viewer] render failed", err);
                cancelAnimationFrame(rafRef.current!);
                setInitError("Render loop crashed");
                setInitialized(false);
                return;
            }
            rafRef.current = requestAnimationFrame(animate);
        };
        animate();

        // Play-Versuch. Die Play-Promise wurde bereits synchron im
        // Click-Handler (startExperience) angestoßen. Hier fangen wir nur
        // den Edge-Case ab, dass das Video zum Zeitpunkt des Klicks noch
        // nicht bereit war. AbortError wird still ignoriert (Race).
        const tryPlay = () => {
            if (!video.paused) return;
            void safeAutoplay(video);
        };
        if (video.readyState >= 2) {
            tryPlay();
        } else {
            video.addEventListener("canplay", tryPlay, { once: true });
        }

        // Debounced Resize + expliziter `orientationchange`-Listener
        // (analog BA360). Guards gegen 0-size-Frames während Reflow +
        // dispatched-fullscreen-Übergänge.
        const resizeRef: { id: number | null } = { id: null };
        const applyResize = () => {
            if (!container || !rendererRef.current || !cameraRef.current) return;
            const w = container.clientWidth;
            const h = container.clientHeight;
            if (!w || !h) return; // Zwischen-Frame mit 0 skippen — kein setSize(0,0)
            rendererRef.current.setSize(w, h, false);
            cameraRef.current.aspect = w / h;
            cameraRef.current.updateProjectionMatrix();
        };
        const onResize = () => {
            if (resizeRef.id !== null) window.clearTimeout(resizeRef.id);
            resizeRef.id = window.setTimeout(() => {
                resizeRef.id = null;
                applyResize();
            }, 150);
        };
        const onOrientation = () => window.setTimeout(() => onResize(), 300);
        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onOrientation);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            if (
                rvfcHandleRef.current != null &&
                typeof rvfcVideo.cancelVideoFrameCallback === "function"
            ) {
                rvfcVideo.cancelVideoFrameCallback(rvfcHandleRef.current);
                rvfcHandleRef.current = null;
            }
            window.removeEventListener("resize", onResize);
            window.removeEventListener("orientationchange", onOrientation);
            if (resizeRef.id !== null) {
                window.clearTimeout(resizeRef.id);
                resizeRef.id = null;
            }
            video.removeEventListener("canplay", tryPlay);
            geometryRef.current?.dispose();
            materialRef.current?.dispose();
            textureRef.current?.dispose();
            posterTextureRef.current?.dispose();
            rendererRef.current?.dispose();
            rendererRef.current = null;
            sceneRef.current = null;
            cameraRef.current = null;
            textureRef.current = null;
            posterTextureRef.current = null;
            geometryRef.current = null;
            materialRef.current = null;
        };
    }, [initialized]);

    // -------- Controls (pointer/wheel/touch) --------
    useEffect(() => {
        if (!initialized) return;
        const container = containerRef.current;
        if (!container) return;

        let dragging = false;
        let lastX = 0;
        let lastY = 0;
        let pinchStart: { d: number; fov: number } | null = null;

        const onPointerDown = (e: PointerEvent) => {
            if (gyroActiveRef.current) return;
            // Klicks auf Control-Bar (Play/Pause/Reset/Fullscreen/Progress-
            // Bar…) sollen weder Drag starten noch Auto-Tour berühren.
            const tgt = e.target as HTMLElement | null;
            if (tgt && tgt.closest(".hf-viewer-controls")) return;
            // Echter Canvas-Drag = User will Raum manuell rotieren:
            //   1) laufenden Auto-Resume-Timer abbrechen (Nutzer greift ein)
            //   2) laufende Auto-Tour temporär stoppen
            cancelAutoResume();
            if (autoRotatingRef.current) stopAutoRotate();
            dragging = true;
            lastX = e.clientX;
            lastY = e.clientY;
            (e.target as Element).setPointerCapture?.(e.pointerId);
        };
        const onPointerMove = (e: PointerEvent) => {
            if (!dragging) return;
            const dx = e.clientX - lastX;
            const dy = e.clientY - lastY;
            lastX = e.clientX;
            lastY = e.clientY;
            const sensitivity = 0.15 * (fovRef.current / 75);
            lonRef.current = (lonRef.current - dx * sensitivity + 360) % 360;
            latRef.current = Math.max(
                -85,
                Math.min(85, latRef.current + dy * sensitivity),
            );
        };
        const onPointerUp = (e: PointerEvent) => {
            // Nur wenn tatsächlich Canvas-Drag stattgefunden hat, den 5-s-
            // Auto-Resume-Timer starten. Controls-Klicks führen den Guard
            // oben zum early-return und laufen nie durch diesen Zweig.
            const wasDragging = dragging;
            dragging = false;
            (e.target as Element).releasePointerCapture?.(e.pointerId);
            if (wasDragging) {
                scheduleAutoResume();
                // Analytics: nur EIN Interaction-Event pro Drag (nicht pro
                // Bewegungs-Frame). Der Hook debounced zusätzlich.
                tracker.onInteraction(lonRef.current, latRef.current, fovRef.current);
            }
        };
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            // Wheel-Zoom = echte Canvas-Interaktion → analog zum Drag pausieren
            // und 5-s-Auto-Resume starten.
            cancelAutoResume();
            if (autoRotatingRef.current) stopAutoRotate();
            fovRef.current = Math.max(
                30,
                Math.min(100, fovRef.current + e.deltaY * 0.05),
            );
            scheduleAutoResume();
            tracker.onInteraction(lonRef.current, latRef.current, fovRef.current);
        };
        const distance = (t1: Touch, t2: Touch) =>
            Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
        const onTouchStart = (e: TouchEvent) => {
            const tgt = e.target as HTMLElement | null;
            const onControls = !!(tgt && tgt.closest(".hf-viewer-controls"));
            if (onControls) return;
            cancelAutoResume();
            if (autoRotatingRef.current) stopAutoRotate();
            if (e.touches.length === 2) {
                pinchStart = {
                    d: distance(e.touches[0], e.touches[1]),
                    fov: fovRef.current,
                };
            }
        };
        const onTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2 && pinchStart) {
                e.preventDefault();
                const d = distance(e.touches[0], e.touches[1]);
                const ratio = pinchStart.d / d;
                fovRef.current = Math.max(
                    30,
                    Math.min(100, pinchStart.fov * ratio),
                );
            }
        };
        const onTouchEnd = (e: TouchEvent) => {
            pinchStart = null;
            const tgt = e.target as HTMLElement | null;
            const onControls = !!(tgt && tgt.closest(".hf-viewer-controls"));
            if (!onControls) scheduleAutoResume();
        };

        container.addEventListener("pointerdown", onPointerDown);
        container.addEventListener("pointermove", onPointerMove);
        container.addEventListener("pointerup", onPointerUp);
        container.addEventListener("pointercancel", onPointerUp);
        container.addEventListener("wheel", onWheel, { passive: false });
        container.addEventListener("touchstart", onTouchStart, {
            passive: true,
        });
        container.addEventListener("touchmove", onTouchMove, {
            passive: false,
        });
        container.addEventListener("touchend", onTouchEnd);
        return () => {
            container.removeEventListener("pointerdown", onPointerDown);
            container.removeEventListener("pointermove", onPointerMove);
            container.removeEventListener("pointerup", onPointerUp);
            container.removeEventListener("pointercancel", onPointerUp);
            container.removeEventListener("wheel", onWheel);
            container.removeEventListener("touchstart", onTouchStart);
            container.removeEventListener("touchmove", onTouchMove);
            container.removeEventListener("touchend", onTouchEnd);
        };
    }, [initialized, cancelAutoResume, scheduleAutoResume, stopAutoRotate]);

    // -------- Video Events --------
    useEffect(() => {
        if (!initialized) return;
        const video = videoRef.current;
        if (!video) return;
        const onPlay = () => {
            setIsPlaying(true);
            tracker.onPlay(video.currentTime);
            // Auto-Tour starten sobald Video wirklich läuft. Guards:
            //   - Gyro aktiv? → Kamera folgt Sensor, keine Rotation.
            //   - Tour läuft bereits? → nichts tun.
            //   - Reduced-Motion-Preference respektiert startAutoRotate selbst.
            if (gyroActiveRef.current) return;
            if (autoRotatingRef.current) return;
            startAutoRotate();
        };
        const onPause = () => {
            setIsPlaying(false);
            // ended-Events lösen ebenfalls pause aus — den doppelten Send
            // vermeiden wir, weil das Tracking `ended` mit einem eigenen
            // Complete-Event dokumentiert (`onEnded`).
            if (!video.ended) {
                tracker.onPause(video.currentTime);
            }
            // Pause = User-Intent, Tour komplett anhalten. Kein Auto-Resume
            // solange das Video pausiert steht.
            cancelAutoResume();
            if (autoRotatingRef.current) stopAutoRotate();
        };
        const onEnded = () => {
            tracker.onComplete(video.duration);
            cancelAutoResume();
            if (autoRotatingRef.current) stopAutoRotate();
        };
        const onCanPlay = () => {
            // `canplay` heißt: Video kann prinzipiell gestartet werden.
            // Der Sphere-Wechsel auf VideoTexture ist BEREITS erfolgt (Sphere
            // startet direkt mit VideoTexture). Hier setzen wir nur das
            // `videoReady`-Flag für interne Logik (Timeline aktiv, etc.).
            setVideoReady(true);
        };
        /** ECHTE First-Paint-Detection.
         *
         * `canplay`/`playing` feuern bevor der Browser den ersten Frame
         * gerendert hat — das ist die Ursache des schwarzen Blitzes.
         * Wir nutzen deshalb `requestVideoFrameCallback` (Chrome, Edge,
         * Safari 17+). Firefox hat's noch nicht: dort greift das
         * `playing`+150ms-Fallback via onPlaying().
         *
         * `presentedFrames >= 1` heißt: mindestens ein Frame ist gemalt.
         * Erst DANN fadet das Poster-Overlay aus. So gibt es keinen
         * schwarzen Zwischenzustand.
         */
        type RVFCMeta = { presentedFrames?: number };
        type RVFCVideo = HTMLVideoElement & {
            requestVideoFrameCallback?: (
                cb: (now: number, meta: RVFCMeta) => void,
            ) => number;
        };
        const rvfcVideo = video as RVFCVideo;
        let rvfcHandle: number | null = null;
        let playingFallbackTimer: number | null = null;
        const markFirstFrame = () => {
            if (playingFallbackTimer !== null) {
                window.clearTimeout(playingFallbackTimer);
                playingFallbackTimer = null;
            }
            setFirstFramePainted(true);
        };
        const onPlaying = () => {
            // Fallback für Firefox (kein requestVideoFrameCallback):
            // 150 ms nach `playing` ist ein Frame quasi immer schon gemalt.
            if (!rvfcVideo.requestVideoFrameCallback) {
                if (playingFallbackTimer === null) {
                    playingFallbackTimer = window.setTimeout(markFirstFrame, 150);
                }
            }
        };
        if (rvfcVideo.requestVideoFrameCallback) {
            const onRVFC = (_now: number, meta: RVFCMeta) => {
                // Manche Browser rufen den Callback einmalig mit presentedFrames=0
                // für „nächster Frame bereit" auf — dann warten wir aufs nächste
                // Callback. Praktisch feuert der zweite Aufruf innerhalb
                // ~16 ms und garantiert echten Paint.
                if ((meta?.presentedFrames || 0) >= 1) {
                    markFirstFrame();
                } else if (rvfcVideo.requestVideoFrameCallback) {
                    rvfcHandle = rvfcVideo.requestVideoFrameCallback(onRVFC);
                }
            };
            rvfcHandle = rvfcVideo.requestVideoFrameCallback(onRVFC);
        }
        const onError = () => {
            const err = video.error;
            if (err && (err.code === 3 || err.code === 4)) {
                console.warn(
                    "[Video360Viewer] media error",
                    err.code,
                    err.message,
                );
                // Wenn wir aus /info wissen, dass das Source-Video zu groß ist
                // und HLS gerade generiert wird, zeigen wir den freundlicheren
                // Optimierungs-Hinweis statt der harten Codec-Meldung.
                if (needsOptimization) {
                    setMediaError(
                        "Dieses Video wird gerade für Web-Wiedergabe optimiert (typisch 3–5 min). Bitte gleich neu laden.",
                    );
                } else {
                    setMediaError(
                        "Video wird von diesem Browser nicht unterstützt (H.264 Codec fehlt oder Auflösung zu hoch).",
                    );
                }
            }
        };
        video.addEventListener("play", onPlay);
        video.addEventListener("pause", onPause);
        video.addEventListener("ended", onEnded);
        video.addEventListener("canplay", onCanPlay);
        video.addEventListener("playing", onCanPlay);
        video.addEventListener("playing", onPlaying);
        video.addEventListener("error", onError);
        // Notfall-Fallback: wenn `canplay` nie feuert (kaputte URL, sehr
        // langsames Netz, Codec-Ablehnung), Spinner spätestens nach 15 s
        // ausblenden — der Poster bleibt als Sphere-Textur sichtbar und
        // der Fehler-Banner wird über onError sowieso gerendert.
        const failsafe = window.setTimeout(() => {
            setVideoReady(true);
            // Nach 15 s spätestens auch das Poster-Overlay ausblenden — sonst
            // hängt es dauerhaft über einem stummen Fehler-Fallback.
            setFirstFramePainted(true);
        }, 15000);
        return () => {
            video.removeEventListener("play", onPlay);
            video.removeEventListener("pause", onPause);
            video.removeEventListener("ended", onEnded);
            video.removeEventListener("canplay", onCanPlay);
            video.removeEventListener("playing", onCanPlay);
            video.removeEventListener("playing", onPlaying);
            video.removeEventListener("error", onError);
            window.clearTimeout(failsafe);
            if (playingFallbackTimer !== null) {
                window.clearTimeout(playingFallbackTimer);
            }
            // rvfcHandle kann nicht sauber gecancelt werden auf allen Browsern,
            // aber setState-Aufrufe nach Unmount sind durch React 18 idempotent.
            void rvfcHandle;
        };
    }, [initialized, needsOptimization, startAutoRotate, stopAutoRotate, cancelAutoResume]);

    // -------- Timeline: Duration + CurrentTime tracking --------
    // Läuft unabhängig von initialized, weil das <video>-Element auch schon
    // vor dem Klick auf „Erlebnis starten" existiert und (auto-)preloadet.
    // Sobald Metadata verfügbar → duration, dann kontinuierliches timeupdate.
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const onDur = () => {
            const d = Number.isFinite(video.duration) ? video.duration : 0;
            setDuration(d);
        };
        const onTime = () => {
            // Während aktivem Seek-Drag NICHT über timeupdate überschreiben,
            // sonst zuckt der Playhead beim Ziehen zurück.
            if (seekingRef.current) return;
            setCurrentTime(video.currentTime || 0);
            // Analytics: Watch-Milestones (25/50/75/100 %) — pro Session
            // + Media nur je einmal (Debounce steckt im Hook).
            if (Number.isFinite(video.duration) && video.duration > 0) {
                tracker.onProgress(video.currentTime || 0, video.duration);
            }
        };
        video.addEventListener("loadedmetadata", onDur);
        video.addEventListener("durationchange", onDur);
        video.addEventListener("timeupdate", onTime);
        // Initial-Sync falls Metadata schon geladen ist
        if (Number.isFinite(video.duration) && video.duration > 0) {
            setDuration(video.duration);
            setCurrentTime(video.currentTime || 0);
        }
        return () => {
            video.removeEventListener("loadedmetadata", onDur);
            video.removeEventListener("durationchange", onDur);
            video.removeEventListener("timeupdate", onTime);
        };
    }, [initialized]);

    // -------- Actions --------
    const startExperience = useCallback(() => {
        // Kein Vorab-Gate mehr. Wir versuchen einfach zu initialisieren; der
        // eigentliche `new WebGLRenderer(...)` steht in try/catch und der
        // Renderloop ebenfalls. Wenn WebGL wirklich nicht geht, wird
        // `initError` gesetzt und die Fallback-Karte gerendert.
        setMediaError(null);
        setInitError(null);
        setNeedsPlayTap(false);
        // Force-Flag für QA erhalten:
        const forced = Boolean(
            (window as unknown as { __HF_FORCE_NO_WEBGL__?: boolean })
                .__HF_FORCE_NO_WEBGL__,
        );
        if (forced) {
            setInitError("WebGL wurde per QA-Flag deaktiviert");
            return;
        }
        // Zusätzlicher **early guard**: WebGL vor dem eigentlichen Init prüfen.
        // Verhindert dass Three.js überhaupt versucht einen Context zu holen,
        // wenn der Browser keinen liefern kann (iOS-Low-Power, Chrome mit
        // deaktivierter HW-Beschleunigung, Playwright-Chromium ohne GPU-Layer).
        // Wenn kein WebGL → ViewerFallback anstatt Runtime-Crash.
        if (!hasWebGLSupport()) {
            setInitError(
                "WebGL ist in diesem Browser nicht verfügbar. Bitte moderneren Browser nutzen oder Hardware-Beschleunigung aktivieren.",
            );
            return;
        }
        // WICHTIG (iOS Safari): video.play() MUSS synchron im User-Gesture-
        // Handler aufgerufen werden, sonst blockt iOS Autoplay UND WebGL
        // pausiert die Video-Frame-Verarbeitung (Black Screen mit Audio).
        // Wir triggern .load() + .play() hier direkt, bevor React die
        // WebGL-Init nach dem Re-Render startet.
        //
        // Bei HLS: KEIN v.load() aufrufen — sonst wird die von hls.js
        // aufgebaute MediaSource entfernt. hls.js hat bereits `attachMedia`
        // gerufen; wir müssen nur `play()` triggern.
        const v = videoRef.current;
        if (v) {
            try {
                v.muted = true;
                v.playsInline = true;
                if (!effectiveHlsSrc && lastSrcRef.current !== src) {
                    // Direkter MP4-Stream, noch nicht via Video-Src-useEffect
                    // vorgeladen: hier load() rufen. Sonst würde der Browser
                    // die src erst beim ersten play() verlangsamt starten.
                    // Falls lastSrcRef bereits gesetzt ist, hat der Src-
                    // useEffect load() schon ausgelöst → doppel-load würde
                    // das laufende preload+play interrupten.
                    lastSrcRef.current = src;
                    v.load();
                }
                currentSrcRef.current = v.currentSrc || v.src || src;
                const p = v.play();
                if (p && typeof p.then === "function") {
                    playInFlightRef.current = p;
                    p.then(() => {
                        setNeedsPlayTap(false);
                    }).catch((err) => {
                        const name = err instanceof Error ? err.name : String(err);
                        if (name === "AbortError") return; // harmlos
                        if (name === "NotAllowedError") {
                            setNeedsPlayTap(true);
                            return;
                        }
                        // Nur echte Media-Errors ins Overlay hochziehen.
                        setMediaError(
                            err instanceof Error ? err.message : String(err),
                        );
                    }).finally(() => {
                        playInFlightRef.current = null;
                    });
                }
            } catch {
                /* ignore, wird im Effekt erneut versucht */
            }
        }
        setInitialized(true);
    }, [effectiveHlsSrc]);

    // Instant-Immersion: wenn `autoPlay=true` und noch nicht initialisiert,
    // startExperience() beim Mount automatisch triggern. `.play()` innerhalb
    // von startExperience() ist bereits per Promise-catch abgesichert und
    // fällt auf `needsPlayTap` zurück, falls der Browser Autoplay blockt.
    useEffect(() => {
        if (!autoPlay) return;
        if (initialized) return;
        // Nur ein Frame Layout-Puffer damit WebGL-Init nicht in eine 0-Size-
        // Container-Race läuft. Kürzer als 120 ms → spürbarer schnellerer
        // Video-Start (kein visueller Unterschied für den User).
        const t = window.setTimeout(() => {
            if (!initialized) startExperience();
        }, 16);
        return () => window.clearTimeout(t);
    }, [autoPlay, initialized, startExperience]);

    // Auto-Tour-Trigger: sobald der Viewer initialisiert ist und das Video
    // ausreichend Daten geladen hat (readyState >= HAVE_CURRENT_DATA),
    // starten wir die sanfte Kamerarotation automatisch. Der Nutzer kann
    // sie danach jederzeit über den Tour-Button ausschalten.
    // `prefers-reduced-motion` wird in `startAutoRotate` selbst respektiert.
    const autoTourFiredRef = useRef(false);
    useEffect(() => {
        if (!autoTour) return;
        if (!initialized) return;
        if (autoTourFiredRef.current) return;
        const v = videoRef.current;
        if (!v) return;
        const kick = () => {
            if (autoTourFiredRef.current) return;
            if (!autoRotatingRef.current) {
                autoTourFiredRef.current = true;
                startAutoRotate();
            }
        };
        if (v.readyState >= 2) {
            // Video hat bereits Frames → nächster Tick reicht.
            const t = window.setTimeout(kick, 0);
            return () => window.clearTimeout(t);
        }
        // Sonst auf `canplay` warten, damit die erste Rotation nicht in
        // Sekunde 0 des Ladens gerendert wird (visuell hakelig).
        v.addEventListener("canplay", kick, { once: true });
        v.addEventListener("playing", kick, { once: true });
        return () => {
            v.removeEventListener("canplay", kick);
            v.removeEventListener("playing", kick);
        };
    }, [autoTour, initialized, startAutoRotate]);

    // Nach HLS→MP4-Fallback nochmal play() triggern (der bestehende
    // MP4-useEffect ruft `.load()`, aber kein `.play()`). Ohne diesen Trigger
    // würde das Video nach dem HLS-Crash pausiert bleiben.
    useEffect(() => {
        if (!hlsFatalFallback) return;
        if (!autoPlay) return;
        const v = videoRef.current;
        if (!v) return;
        // Race-safe: safeAutoplay wartet auf canplay + filtert AbortError.
        void safeAutoplay(v);
    }, [hlsFatalFallback, autoPlay, safeAutoplay]);

    const retryPlayWithGesture = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        setNeedsPlayTap(false);
        setMediaError(null);
        v.muted = true;
        v.playsInline = true;
        void safeAutoplay(v);
    }, [safeAutoplay]);
    const togglePlay = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
            void safeAutoplay(v);
        } else {
            v.pause();
            // Master-Stop: Video-Pause hält auch alle Kamera-Automatik an.
            // Auto-Rotate und Gyro sind mutually exclusive (nur eines aktiv),
            // aber wir stoppen defensive beides, damit egal welches Modus lief.
            if (autoRotatingRef.current) {
                stopAutoRotate();
            }
            if (gyroActiveRef.current) {
                detachGyro();
            }
        }
    }, [safeAutoplay, stopAutoRotate, detachGyro]);
    const toggleMute = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        setIsMuted(v.muted);
    }, []);
    const toggleFullscreen = useCallback(async () => {
        const el = containerRef.current;
        if (!el) return;
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            } else {
                await el.requestFullscreen?.();
                tracker.onFullscreen();
            }
        } catch {
            /* ignore */
        }
    }, [tracker]);
    const resetView = useCallback(() => {
        // Toggle-Fall: läuft Auto-Rotate → nur stoppen, kein Neustart.
        if (autoRotatingRef.current) {
            console.log("[Video360Viewer] resetView clicked → toggling auto-rotate OFF");
            stopAutoRotate();
            return;
        }
        console.log("[Video360Viewer] resetView clicked → lon=180 lat=0 fov=75 → auto-rotate");
        // Sanfter Reset: animiere lon/lat/fov über ~800ms zu Neutral, danach
        // Auto-Rotate starten. Wir nutzen einen eigenen kleinen rAF-Tween,
        // damit der Nutzer den Rückflug sieht statt eines harten Sprungs.
        const startLon = lonRef.current;
        const startLat = latRef.current;
        const startFov = fovRef.current;
        // Kürzester Weg für lon (mod 360)
        let deltaLon = 180 - startLon;
        if (deltaLon > 180) deltaLon -= 360;
        if (deltaLon < -180) deltaLon += 360;
        const t0 = performance.now();
        const DUR = 800;
        const tweenTick = (now: number) => {
            const t = Math.min((now - t0) / DUR, 1);
            // easeInOutCubic
            const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            lonRef.current = (startLon + deltaLon * eased + 360) % 360;
            latRef.current = startLat + (0 - startLat) * eased;
            fovRef.current = startFov + (75 - startFov) * eased;
            if (cameraRef.current) {
                cameraRef.current.fov = fovRef.current;
                cameraRef.current.updateProjectionMatrix();
            }
            if (t < 1) {
                requestAnimationFrame(tweenTick);
            } else {
                // Reset abgeschlossen → Auto-Rotate starten
                startAutoRotate();
            }
        };
        requestAnimationFrame(tweenTick);
    }, [startAutoRotate, stopAutoRotate]);

    // -------- Timeline Seek --------
    const seekToClientX = useCallback((clientX: number) => {
        const el = timelineRef.current;
        const v = videoRef.current;
        if (!el || !v || !Number.isFinite(v.duration) || v.duration <= 0) return;
        const rect = el.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const t = ratio * v.duration;
        v.currentTime = t;
        setCurrentTime(t);
    }, []);
    const onTimelinePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const el = timelineRef.current;
        if (!el) return;
        seekingRef.current = true;
        el.setPointerCapture(e.pointerId);
        seekToClientX(e.clientX);
    }, [seekToClientX]);
    const onTimelinePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!seekingRef.current) return;
        seekToClientX(e.clientX);
    }, [seekToClientX]);
    const onTimelinePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const el = timelineRef.current;
        if (!el) return;
        seekingRef.current = false;
        try { el.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    }, []);
    const onTimelineKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        const v = videoRef.current;
        if (!v || !Number.isFinite(v.duration) || v.duration <= 0) return;
        const step = e.shiftKey ? 10 : 5;
        if (e.key === "ArrowLeft") {
            e.preventDefault();
            v.currentTime = Math.max(0, v.currentTime - step);
            setCurrentTime(v.currentTime);
        } else if (e.key === "ArrowRight") {
            e.preventDefault();
            v.currentTime = Math.min(v.duration, v.currentTime + step);
            setCurrentTime(v.currentTime);
        } else if (e.key === "Home") {
            e.preventDefault();
            v.currentTime = 0;
            setCurrentTime(0);
        } else if (e.key === "End") {
            e.preventDefault();
            v.currentTime = v.duration;
            setCurrentTime(v.duration);
        }
    }, []);
    const toggleGyro = useCallback(async () => {
        if (gyroActiveRef.current) {
            detachGyro();
            return;
        }
        const DOE = window.DeviceOrientationEvent as unknown as {
            requestPermission?: () => Promise<"granted" | "denied">;
        };
        if (DOE && typeof DOE.requestPermission === "function") {
            try {
                const state = await DOE.requestPermission();
                if (state !== "granted") {
                    setGyroOn(false);
                    return;
                }
            } catch {
                setGyroOn(false);
                return;
            }
        }
        // Mutual Exclusion: Auto-Rotate läuft? → sofort stoppen, bevor Gyro
        // die Kamera übernimmt. Sonst ringen beide gleichzeitig um lon/lat.
        if (autoRotatingRef.current) {
            stopAutoRotate();
        }
        const handler = (ev: DeviceOrientationEvent) => {
            if (ev.alpha == null || ev.beta == null || ev.gamma == null) return;
            lonRef.current = (360 - ev.alpha + 360) % 360;
            latRef.current = Math.max(-85, Math.min(85, ev.beta - 90));
        };
        window.addEventListener("deviceorientation", handler, true);
        gyroHandlerRef.current = handler;
        gyroActiveRef.current = true;
        setGyroOn(true);
    }, [stopAutoRotate]);

    // Cleanup bei Unmount: Listener zwingend entfernen, sonst leakt der Handler
    // über die Component-Lebensdauer hinaus und Gyro läuft "im Hintergrund".
    useEffect(() => {
        return () => {
            detachGyro();
        };
    }, [detachGyro]);

    // Device-Detection: Gyro-Button nur auf Mobile/Tablet mit Orientation-API
    const showGyro = useHasGyroscope();

    // -------- Render --------
    // Robustheit: leere / ungültige src → Not-Available-Karte,
    // niemals ein <video src=""> rendern.
    const hasSrc = typeof src === "string" && src.trim().length > 0;
    if (!hasSrc) {
        console.warn(
            `[Video360Viewer] leere src (testIdPrefix=${testIdPrefix}) — media nicht verfügbar`,
        );
        return (
            <ViewerFallback
                testId={`${testIdPrefix}-unavailable`}
                title="Video derzeit nicht verfügbar"
                message="Diese 360°-Aufnahme wurde noch nicht vollständig verarbeitet oder ist im Speicher nicht mehr auffindbar. Bitte kontaktiere den Administrator, falls der Zustand länger anhält."
            />
        );
    }
    if (initError) {
        return (
            <ViewerFallback
                testId={`${testIdPrefix}-fallback`}
                title="360°-Video kann nicht gestartet werden"
                message="Der Browser konnte keinen WebGL-Kontext für das 360°-Video anlegen. Bitte prüfe, ob Hardware-Beschleunigung aktiv ist, oder öffne die Seite in einem anderen Browser (Chrome, Firefox, Safari, Edge)."
            />
        );
    }

    return (
        <div
            ref={containerRef}
            className={fillParent ? "hf-viewer-shell w-full h-full" : "hf-viewer-shell w-full aspect-[16/9] sm:aspect-[21/9]"}
            data-testid={`${testIdPrefix}-shell`}
        >
            <video
                ref={videoRef}
                // KEIN src-Attribut wenn HLS aktiv oder Info-Fetch noch läuft.
                // Ohne diese Vorsichtsmaßnahme lädt der Browser sofort die
                // (evtl. undekodierbare 8K-)MP4, wirft error.code=4 und wir
                // sehen den Fehler noch bevor hls.js attach kann.
                {...(shouldRenderMp4Src ? { src } : {})}
                poster={poster}
                playsInline
                crossOrigin="anonymous"
                muted
                preload="auto"
                loop={loop}
                onEnded={() => { if (!loop) onEnded?.(); }}
                // WICHTIG (iOS): NICHT display:none — iOS pausiert
                // Frame-Decoding für versteckte Video-Elemente, wodurch
                // WebGL-Texturen schwarz bleiben. Stattdessen ein 1px-
                // Offscreen-Element, das trotzdem im DOM & sichtbar ist.
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "1px",
                    height: "1px",
                    opacity: 0,
                    pointerEvents: "none",
                    zIndex: -1,
                }}
                data-testid={`${testIdPrefix}-media`}
            />
            {initialized && (
                <canvas
                    ref={canvasRef}
                    style={{ width: "100%", height: "100%", display: "block" }}
                    data-testid={`${testIdPrefix}-canvas`}
                />
            )}

            {/* Poster-Overlay (Layer 1): rendert einen DOM-basierten Poster-
                <img> + goldenen Spinner unten rechts ÜBER der Three.js-Sphere
                (Layer 2). Das eliminiert das Flackern beim Modal-Öffnen:
                – kein „schwarzer Zwischenzustand" bevor die WebGL-Textur da ist
                – kein „schwarzer Blitz" nach Poster→Video-Swap, denn wir
                  fadem ERST wenn `requestVideoFrameCallback` einen echten
                  ersten Frame gemeldet hat.
                `pointer-events: none` → Drag-Gesten erreichen die Sphere. */}
            {initialized && (
                <div
                    className={`hf-poster-overlay${firstFramePainted ? " hf-poster-overlay--hidden" : ""}`}
                    data-testid={`${testIdPrefix}-poster-overlay`}
                    aria-hidden={firstFramePainted}
                >
                    {poster && (
                        <img
                            src={poster}
                            alt=""
                            className="hf-poster-overlay-img"
                            onError={(e) => {
                                // Poster-Load-Fehler: <img> ausblenden,
                                // sanfter Dark-Ink-Farbverlauf bleibt sichtbar
                                (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                            draggable={false}
                        />
                    )}
                    <div className="hf-poster-overlay-tint" aria-hidden />
                    <div className="hf-poster-overlay-spinner-wrap">
                        <div className="hf-poster-overlay-spinner" aria-hidden />
                        <div className="hf-poster-overlay-spinner-label">
                            Video wird geladen …
                        </div>
                    </div>
                </div>
            )}

            {!initialized && (
                <button
                    type="button"
                    onClick={startExperience}
                    data-testid={`${testIdPrefix}-poster-play`}
                    className="absolute inset-0 z-[3] flex items-center justify-center group cursor-pointer"
                    style={{
                        backgroundImage: poster ? `url(${poster})` : undefined,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundColor: "hsl(var(--hf-dark))",
                    }}
                    aria-label="360°-Video-Erlebnis starten"
                >
                    <span className="absolute inset-0 bg-[rgba(14,23,38,0.35)] group-hover:bg-[rgba(14,23,38,0.2)] transition-colors" />
                    <span className="relative flex items-center justify-center">
                        <span
                            className="flex items-center justify-center w-20 h-20 rounded-full transition-transform group-hover:scale-105"
                            style={{ background: "hsl(var(--hf-gold))" }}
                        >
                            <Play
                                size={30}
                                style={{ color: "hsl(var(--hf-ink))" }}
                                fill="currentColor"
                            />
                        </span>
                    </span>
                </button>
            )}

            {mediaError && (
                <div
                    className="absolute top-4 right-4 z-[6] max-w-xs text-xs px-3 py-2 rounded flex flex-col gap-2 items-start"
                    style={{
                        background: needsOptimization
                            ? "hsl(var(--hf-ink) / 0.92)"
                            : "hsl(var(--hf-danger) / 0.9)",
                        color: needsOptimization
                            ? "hsl(var(--hf-gold-soft))"
                            : "hsl(var(--hf-bg))",
                        border: needsOptimization
                            ? "1px solid hsl(var(--hf-gold-soft) / 0.4)"
                            : undefined,
                    }}
                    data-testid={`${testIdPrefix}-error`}
                >
                    <span>{mediaError}</span>
                    {needsOptimization && (
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="px-2 py-1 rounded-sm text-[10px] tracking-[0.14em] uppercase"
                            style={{
                                background: "hsl(var(--hf-gold))",
                                color: "hsl(var(--hf-ink))",
                            }}
                            data-testid={`${testIdPrefix}-reload`}
                        >
                            Neu laden
                        </button>
                    )}
                </div>
            )}

            {initialized && needsPlayTap && (
                <button
                    type="button"
                    onClick={retryPlayWithGesture}
                    data-testid={`${testIdPrefix}-retry-play`}
                    className="absolute inset-0 z-[5] flex items-center justify-center group cursor-pointer"
                    style={{
                        background: "rgba(14,23,38,0.55)",
                    }}
                    aria-label="Video jetzt abspielen"
                >
                    <span className="relative flex flex-col items-center gap-4">
                        <span
                            className="flex items-center justify-center w-20 h-20 rounded-full transition-transform group-hover:scale-105"
                            style={{ background: "hsl(var(--hf-gold))" }}
                        >
                            <Play
                                size={30}
                                style={{ color: "hsl(var(--hf-ink))" }}
                                fill="currentColor"
                            />
                        </span>
                        <span
                            className="hf-eyebrow"
                            style={{ color: "hsl(var(--hf-gold-soft))" }}
                        >
                            Tippen zum Abspielen
                        </span>
                    </span>
                </button>
            )}

            {initialized && (
                <div className="hf-viewer-controls">
                    {duration > 0 && (
                        <div
                            ref={timelineRef}
                            className="hf-viewer-timeline"
                            role="slider"
                            tabIndex={0}
                            aria-label="Video-Zeitleiste"
                            aria-valuemin={0}
                            aria-valuemax={Math.round(duration)}
                            aria-valuenow={Math.round(currentTime)}
                            aria-valuetext={`${Math.floor(currentTime)} von ${Math.floor(duration)} Sekunden`}
                            onPointerDown={onTimelinePointerDown}
                            onPointerMove={onTimelinePointerMove}
                            onPointerUp={onTimelinePointerUp}
                            onPointerCancel={onTimelinePointerUp}
                            onKeyDown={onTimelineKeyDown}
                            data-testid={`${testIdPrefix}-timeline`}
                        >
                            <div className="hf-viewer-timeline-rail">
                                <div
                                    className="hf-viewer-timeline-progress"
                                    style={{
                                        width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                                    }}
                                    data-testid={`${testIdPrefix}-timeline-progress`}
                                />
                                <div
                                    className="hf-viewer-timeline-thumb"
                                    style={{
                                        left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                                    }}
                                    data-testid={`${testIdPrefix}-timeline-thumb`}
                                />
                            </div>
                        </div>
                    )}
                    <div className="hf-viewer-controls-row">
                        <button
                            type="button"
                            onClick={togglePlay}
                            className="hf-viewer-ctrl-btn"
                            disabled={!videoReady}
                            title={isPlaying ? "Pause" : "Abspielen"}
                            aria-label={isPlaying ? "Pause" : "Abspielen"}
                            data-testid={`${testIdPrefix}-btn-play`}
                        >
                            {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                            <span className="hf-viewer-ctrl-label">
                                {isPlaying ? "Pause" : "Play"}
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={toggleMute}
                            className="hf-viewer-ctrl-btn"
                            title={isMuted ? "Ton an" : "Ton aus"}
                            aria-label={isMuted ? "Ton einschalten" : "Ton ausschalten"}
                            data-testid={`${testIdPrefix}-btn-mute`}
                        >
                            {isMuted ? (
                                <VolumeX size={18} />
                            ) : (
                                <Volume2 size={18} />
                            )}
                            <span className="hf-viewer-ctrl-label">Ton</span>
                        </button>
                        <button
                            type="button"
                            onClick={resetView}
                            className={
                                autoRotating
                                    ? "hf-viewer-ctrl-btn hf-autorotate-active"
                                    : "hf-viewer-ctrl-btn"
                            }
                            title={autoRotating ? "Auto-Tour stoppen" : "Auto-Tour starten"}
                            aria-label={
                                autoRotating
                                    ? "Auto-Rotation stoppen"
                                    : "Blick zurücksetzen und Auto-Tour starten"
                            }
                            aria-pressed={autoRotating}
                            data-testid={`${testIdPrefix}-btn-reset`}
                        >
                            <RotateCcw size={18} />
                            <span className="hf-viewer-ctrl-label">Tour</span>
                        </button>
                        {showGyro && (
                            <button
                                type="button"
                                onClick={toggleGyro}
                                className={
                                    gyroOn
                                        ? "hf-viewer-ctrl-btn hf-viewer-ctrl-btn-gyro hf-viewer-ctrl-btn--active"
                                        : "hf-viewer-ctrl-btn hf-viewer-ctrl-btn-gyro"
                                }
                                title={gyroOn ? "Gyroskop aus" : "Gyroskop an"}
                                aria-label={gyroOn ? "Gyroskop aus" : "Gyroskop an"}
                                aria-pressed={gyroOn}
                                data-testid={`${testIdPrefix}-btn-gyro`}
                            >
                                <Compass size={18} />
                                <span className="hf-viewer-ctrl-label">Gyro</span>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={toggleFullscreen}
                            className="hf-viewer-ctrl-btn"
                            title={isFullscreen ? "Vollbild verlassen" : "Vollbild"}
                            aria-label={
                                isFullscreen ? "Vollbild verlassen" : "Vollbild"
                            }
                            data-testid={`${testIdPrefix}-btn-fullscreen`}
                        >
                            {isFullscreen ? (
                                <Minimize2 size={18} />
                            ) : (
                                <Maximize2 size={18} />
                            )}
                            <span className="hf-viewer-ctrl-label">Vollbild</span>
                        </button>
                    </div>
                </div>
            )}
            {/* Interaction-Hint-Overlay: permanent sichtbar sobald der Viewer
                initialisiert ist. KEIN isPlaying-Gate — die Icons sind eine
                dauerhafte Bedienungs-Hilfe (opacity 0.75, pointer-events:none,
                background transparent). Der Wrapper legt sich also dezent über
                die Canvas ohne sie abzudunkeln. */}
            {initialized && <InteractionHintOverlay testIdPrefix={testIdPrefix} />}
        </div>
    );
}
